import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
    FormControl,
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators
} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {DataService} from "../../../services/data.service";
import {AccessService} from "../../../services/access.service";
import {ViewMode} from "../../interfaces";
import Swal from "sweetalert2";
import {ToastrService} from "ngx-toastr";
import {Subscription} from "rxjs";
import {DatePipe, NgForOf, NgIf} from "@angular/common";
import {YesNoPipe} from "../../../pipes/yes-no.pipe";

@Component({
    selector: 'app-user-detail',
    templateUrl: './user-detail.component.html',
    imports: [
        NgIf,
        ReactiveFormsModule,
        NgForOf,
        DatePipe,
        YesNoPipe,
        FormsModule
    ],
    styleUrl: './user-detail.component.scss'
})
export class UserDetailComponent implements OnInit, OnDestroy {

    accessService = inject(AccessService);
    private router = inject(Router);
    private fb = inject(UntypedFormBuilder);
    private dataService = inject(DataService);
    private route = inject(ActivatedRoute);
    private toastrService = inject(ToastrService);

    readonly ViewMode = ViewMode;

    form!: UntypedFormGroup;
    id_user!: string;
    viewMode!: ViewMode;
    user: any;
    departments: any[];
    rights: any[];
    passwordChanges$!: Subscription;

    ngOnInit(): void {
        this.id_user = this.route.snapshot.paramMap.get('id');
        this.viewMode = this.id_user == '0' ? ViewMode.INSERT : ViewMode.VIEW;

        const password = new FormControl('', Validators.compose([Validators.required, Validators.min(6)]));
        const confirmPassword = new FormControl('', [
            Validators.required,
            (control: FormControl): { [key: string]: boolean } => {
                if (!control.parent) {
                    return null;
                }

                const email = control.parent.get('password');
                const confirm = control;
                if (!email || !confirm) return null;
                return email.value === confirm.value ? null : {nomatch: true};
            }]
        );

        // make password optional when not inserting
        if (this.id_user !== '0') {
            password.setValidators(Validators.min(6));
            password.updateValueAndValidity();

            confirmPassword.setValidators((control: FormControl): { [key: string]: boolean } => {
                if (!control.parent) {
                    return null;
                }

                const email = control.parent.get('password');
                const confirm = control;
                if (!email || !confirm) return null;
                return email.value === confirm.value ? null : {nomatch: true};
            });
            confirmPassword.updateValueAndValidity();
        }

        this.form = this.fb.group({
            id: [null],
            id_department: [''],
            firstname: [null, Validators.required],
            lastname: [null, Validators.required],
            email: [null, [Validators.required, Validators.email]],
            phone: [null, Validators.required],
            password,
            confirmPassword,
            last_login: [null],
            department: [null],
            active: [true],
        });

        this.passwordChanges$ = this.form.get('password').valueChanges.subscribe(val => {
            this.form.get('confirmPassword').updateValueAndValidity();
        });

        if (this.id_user !== '0') {
            this.getUser();
        } else {
            this.dataService.request('Right/listAll').subscribe(response => {
                this.rights = response.rights;

                this.rights.forEach(right => {
                    right.granted = false;
                })
            });
        }

        this.dataService.request('Department/listForUser').subscribe(response => {
            this.departments = response.departments;
        });
    }

    ngOnDestroy() {
        this.passwordChanges$.unsubscribe();
    }

    getUser(): void {
        this.dataService.request('User/get', {
            id_user: this.id_user
        }).subscribe(response => {
            this.user = response.user;

            this.dataService.request('Right/listAll').subscribe(response => {
                this.rights = response.rights;

                this.rights.forEach(right => {
                    right.granted = this.user.rights.includes(right.code);
                })
            });

            this.patchForm();
        });
    }

    patchForm(): void {
        this.form.patchValue({
            id: this.user.id,
            id_department: this.user.id_department == null ? '' : this.user.id_department,
            firstname: this.user.firstname,
            lastname: this.user.lastname,
            email: this.user.email,
            phone: this.user.phone,
            last_login: this.user.last_login,
            department: this.user.department,
            active: this.user.active == '1',
        });
    }

    save(): void {
        this.dataService.request('User/save', {
            user: this.form.getRawValue(),
            new_rights: this.rights.filter(right => right.granted).map(right => right.id),
        }).subscribe(() => {
            this.router.navigate(['/user']);
            this.toastrService.success('Der Benutzer wurde angelegt');
        });
    }

    update(): void {
        this.dataService.request('User/update', {
            user: this.form.getRawValue(),
            new_rights: this.rights.filter(right => right.granted).map(right => right.id),
        }).subscribe(() => {
            this.setMode(ViewMode.VIEW);
            this.getUser();
            this.toastrService.success('Der Benutzer wurde aktualisiert');
        });
    }

    cancel(): void {
        this.router.navigate(['/user']);
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
    }

    delete(): void {
        Swal.fire({
            title: 'Sicher?',
            text: 'Der Benutzer wird gelöscht.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('User/delete', {
                    id_user: this.id_user
                }).subscribe(() => {
                    this.cancel();
                    this.toastrService.success('Der Benutzer wurde gelöscht');
                });
            }
        });
    }

    toggleRights(granted) {
        this.rights.forEach(right => right.granted = granted);
    }

}
